import React, { useState, useEffect } from "react";
import { Modal, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { geocode, getMonthlyClimateAverages, FAHRENHEIT_COUNTRIES } from "../../Models/ClimateData";
import "./Modal.css";
import "./ClimateDialog.css";

// Fetches its own data on show — same fetch-on-show pattern as
// TrailSearchModal.js. Open-Meteo is free/keyless; see Models/ClimateData.js
// for why this uses the historical archive (typical climate), not
// Open-Meteo's separate future-projections Climate API.
//
// lat/lon are optional — passed by Home.js's "climatelink" scheme, which
// already has a specific place's exact coordinates and can skip geocoding
// entirely (more precise than re-deriving them from city/country text).
const ClimateDialog = ({ show, onHide, city, country, lat, lon }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [months, setMonths] = useState(null);
  const [locationName, setLocationName] = useState([city, country].filter(Boolean).join(", "));
  const [geo, setGeo] = useState(null);
  const [unit, setUnit] = useState("celsius");
  // Open-Meteo's historical archive has a real cold-start cost: the FIRST
  // request for a given location+variable combo can take anywhere from
  // ~30s to 2+ minutes (measured), then ~1s on every repeat once it's
  // cached server-side. A bare spinner reads as "stuck" well before that —
  // this flips on after a few seconds to explain the wait instead of
  // leaving it a mystery. Found 2026-09-14.
  const [slowHint, setSlowHint] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  // Resolves a {lat, lon, country} once per open/destination — either
  // straight from props (climatelink) or via geocoding (destination
  // picker) — and picks a sensible default unit from the country (the user
  // can still toggle it either way).
  useEffect(() => {
    if (!show) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setMonths(null);
    setGeo(null);
    setLocationName([city, country].filter(Boolean).join(", "));

    if (lat != null && lon != null) {
      setUnit(FAHRENHEIT_COUNTRIES.has(country) ? "fahrenheit" : "celsius");
      setGeo({ lat, lon, country });
      return;
    }

    (async () => {
      try {
        const g = await geocode(city, country);
        if (cancelled) return;
        if (!g) {
          setError("Could not find that location.");
          setLoading(false);
          return;
        }
        setLocationName(g.resolvedName);
        setUnit(FAHRENHEIT_COUNTRIES.has(g.country) ? "fahrenheit" : "celsius");
        setGeo(g);
      } catch (e) {
        if (!cancelled) {
          setError(e.message || "Failed to look up that location.");
          setLoading(false);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [show, city, country, lat, lon]);

  // Re-fetches whenever the resolved location changes, or the user toggles
  // the unit — Open-Meteo converts server-side, so this is a real refetch,
  // not a client-side C-to-F conversion.
  useEffect(() => {
    if (!geo) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSlowHint(false);
    const slowTimer = setTimeout(() => { if (!cancelled) setSlowHint(true); }, 8000);

    (async () => {
      try {
        const data = await getMonthlyClimateAverages(geo.lat, geo.lon, 10, unit);
        if (!cancelled) setMonths(data);
      } catch (e) {
        if (!cancelled) setError(e.message || "Failed to fetch climate data.");
      } finally {
        if (!cancelled) setLoading(false);
        clearTimeout(slowTimer);
      }
    })();

    return () => { cancelled = true; clearTimeout(slowTimer); };
  }, [geo, unit, retryToken]);

  const fmt = (n, digits = 0) => (n == null ? "—" : n.toFixed(digits));

  return (
    <div className="modal">
      <Modal
        show={show}
        onHide={onHide}
        animation={false}
        dialogClassName="cld-dialog"
        aria-labelledby="cld-title"
        centered
      >
        <Modal.Body style={{ padding: 0 }}>
          <div className="cld-shell">

            <div className="cld-header">
              <div className="cld-header-icon">
                <FontAwesomeIcon icon={solid("temperature-half")} />
              </div>
              <div className="cld-header-text">
                <h2 className="cld-title" id="cld-title">Climate</h2>
                {locationName && <span className="cld-subtitle">{locationName}</span>}
              </div>
              <div className="cld-unit-toggle">
                <button
                  className={`cld-unit-btn${unit === "celsius" ? " active" : ""}`}
                  onClick={() => setUnit("celsius")}
                  disabled={!geo}
                >
                  °C
                </button>
                <button
                  className={`cld-unit-btn${unit === "fahrenheit" ? " active" : ""}`}
                  onClick={() => setUnit("fahrenheit")}
                  disabled={!geo}
                >
                  °F
                </button>
              </div>
              <button className="cld-close" onClick={onHide}>
                <FontAwesomeIcon icon={solid("xmark")} />
              </button>
            </div>

            <div className="cld-body">
              {loading && (
                <div className="cld-center">
                  <Spinner animation="border" size="sm" />
                  <span>
                    {slowHint
                      ? "Still fetching… a new location can take up to a couple of minutes the first time, then it's fast."
                      : "Fetching climate data…"}
                  </span>
                </div>
              )}
              {!loading && error && (
                <div className="cld-center cld-error">
                  <div>{error}</div>
                  <button className="btn btn-outline-secondary btn-sm" style={{ marginTop: "0.5rem" }} onClick={() => setRetryToken((t) => t + 1)}>
                    Try again
                  </button>
                </div>
              )}
              {!loading && months && (() => {
                // Skip the Snow column entirely for destinations where it
                // never happens — an all-zero column is just clutter.
                const hasSnow = months.some((m) => m.avgSnow > 0.05);
                return (
                <div className="cld-table-wrap">
                <table className="cld-table">
                  <thead>
                    <tr>
                      <th>Month</th>
                      <th>High</th>
                      <th>Low</th>
                      <th>Rain</th>
                      {hasSnow && <th>Snow</th>}
                      <th>Humidity</th>
                      <th>Sun</th>
                    </tr>
                  </thead>
                  <tbody>
                    {months.map((m) => (
                      <tr key={m.month}>
                        <td>{m.monthName}</td>
                        <td>{fmt(m.avgHigh)}{m.tempUnit}</td>
                        <td>{fmt(m.avgLow)}{m.tempUnit}</td>
                        <td>{fmt(m.avgPrecip, m.precipUnit === "in" ? 1 : 0)} {m.precipUnit}</td>
                        {hasSnow && (
                          <td>{fmt(m.avgSnow, m.precipUnit === "in" ? 1 : 0)} {m.precipUnit}</td>
                        )}
                        <td>{fmt(m.avgHumidity)}%</td>
                        <td>{fmt(m.avgSunshineHours, 1)}h</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                </div>
                );
              })()}
              <p className="cld-footnote">
                Averages from the last 10 years of daily weather data (Open-Meteo).
              </p>
            </div>

          </div>
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default ClimateDialog;
